import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { getTenantById } from '../../api/backendApi';
import useUserShortcuts from '../../hooks/shortcuts/useUserShortcuts';
import ShortcutEditorDialog from '../HomeLauncher/components/ShortcutEditorDialog';
import ShortcutTile from '../HomeLauncher/components/ShortcutTile';
import {
  SCOPE_SHARED,
  moveShortcutOrder,
  moveTabOrder,
  newEntityId,
  replaceTabShortcuts,
  shortcutsForTab,
  utcNow,
} from '../../services/shortcuts/shortcutModel';

export default function AdminShortcutsPage({
  embedded = false,
  tenantId: tenantIdProp = '',
  tenantName: tenantNameProp = '',
} = {}) {
  const { currentUser, userData, tenantId: authTenantId } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const tenantId = String(tenantIdProp || dealerAdmin.tenantId || authTenantId || userData?.tenantId || '').trim();
  const [recordName, setRecordName] = useState('');
  const knownName = [tenantNameProp, dealerAdmin.tenantName, userData?.tenantName]
    .map(value => String(value || '').trim())
    .find(value => value && value !== tenantId) || '';
  const tenantName = knownName || recordName;

  useEffect(() => {
    if (!tenantId || knownName) {
      setRecordName('');
      return undefined;
    }
    let cancelled = false;
    getTenantById(tenantId)
      .then(response => {
        const data = response?.data && typeof response.data === 'object' ? response.data : response;
        const name = String(data?.name || data?.displayName || '').trim();
        if (!cancelled) setRecordName(name && name !== tenantId ? name : '');
      })
      .catch(() => {
        if (!cancelled) setRecordName('');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, knownName]);
  const shortcuts = useUserShortcuts({
    uid: currentUser?.uid || null,
    tenantId,
  });
  const sharedTabs = shortcuts.shared?.tabs || [];
  const [selectedTabId, setSelectedTabId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [editorShortcut, setEditorShortcut] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [suppressOpen, setSuppressOpen] = useState(false);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );

  useEffect(() => {
    if (!sharedTabs.some(tab => tab.id === selectedTabId)) {
      setSelectedTabId(sharedTabs[0]?.id || null);
    }
  }, [sharedTabs, selectedTabId]);

  const selectedTab = sharedTabs.find(tab => tab.id === selectedTabId) || null;
  useEffect(() => {
    setDraftTitle(selectedTab?.title || '');
  }, [selectedTab?.id, selectedTab?.title]);

  const tabShortcuts = useMemo(
    () => shortcutsForTab(shortcuts.shared, selectedTabId),
    [shortcuts.shared, selectedTabId]
  );
  const canManage = Boolean(shortcuts.canManageShared);

  const saveShared = next => shortcuts.saveSharedDocument({
    ...shortcuts.shared,
    ...next,
    scope: SCOPE_SHARED,
    tenantId,
  });

  const addTab = async () => {
    const now = utcNow();
    const tab = {
      id: newEntityId('stab'),
      title: 'New tab',
      order: sharedTabs.length,
      scope: SCOPE_SHARED,
      createdAt: now,
      updatedAt: now,
    };
    await saveShared({ tabs: [...sharedTabs, tab] });
    setSelectedTabId(tab.id);
  };

  const renameSelected = async () => {
    const title = draftTitle.trim();
    if (!selectedTab || !title || title === selectedTab.title) return;
    await saveShared({
      tabs: sharedTabs.map(tab => (
        tab.id === selectedTab.id ? { ...tab, title, updatedAt: utcNow() } : tab
      )),
    });
  };

  const deleteSelected = async () => {
    if (!selectedTab) return;
    await saveShared({
      tabs: sharedTabs.filter(tab => tab.id !== selectedTab.id),
      shortcuts: (shortcuts.shared.shortcuts || []).filter(item => item.tabId !== selectedTab.id),
    });
  };

  const moveSelectedTab = async direction => {
    if (!selectedTab) return;
    const ordered = [...sharedTabs].sort((a, b) => a.order - b.order);
    const index = ordered.findIndex(tab => tab.id === selectedTab.id);
    const neighbor = ordered[index + direction];
    if (!neighbor) return;
    await saveShared({ tabs: moveTabOrder(sharedTabs, selectedTab.id, neighbor.id) });
  };

  const saveShortcut = async input => {
    if (!selectedTab) throw new Error('Choose a tab first');
    const now = utcNow();
    const existing = (shortcuts.shared.shortcuts || []).find(item => item.id === input.id);
    const next = existing
      ? (shortcuts.shared.shortcuts || []).map(item => (
        item.id === existing.id
          ? { ...item, ...input, tabId: selectedTab.id, scope: SCOPE_SHARED, updatedAt: now }
          : item
      ))
      : [
        ...(shortcuts.shared.shortcuts || []),
        {
          id: newEntityId('ssc'),
          tabId: selectedTab.id,
          label: input.label,
          url: input.url,
          iconType: input.iconType,
          iconKey: input.iconKey,
          iconImageAssetId: input.iconImageAssetId,
          order: tabShortcuts.length,
          scope: SCOPE_SHARED,
          createdAt: now,
          updatedAt: now,
        },
      ];
    await saveShared({ shortcuts: next });
  };

  const deleteShortcut = async shortcutId => {
    await saveShared({
      shortcuts: (shortcuts.shared.shortcuts || []).filter(item => item.id !== shortcutId),
    });
  };

  const handleDragEnd = async event => {
    setSuppressOpen(true);
    window.setTimeout(() => setSuppressOpen(false), 0);
    const { active, over } = event;
    if (!selectedTab || !over || active.id === over.id) return;
    const reordered = moveShortcutOrder(tabShortcuts, active.id, over.id);
    await saveShared({
      shortcuts: replaceTabShortcuts(shortcuts.shared, selectedTab.id, reordered).shortcuts,
    });
  };

  const content = (
    <>
      <Box sx={{ p: embedded ? 1 : 3, maxWidth: 1100 }}>
        {embedded ? null : <Typography variant="h5" component="h1">Shortcuts</Typography>}
        {tenantName ? (
          <Typography variant="body1" sx={{ mt: 1 }}>{tenantName}</Typography>
        ) : !tenantId ? (
          <Typography variant="body1" sx={{ mt: 1 }}>No tenant selected</Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          These shortcuts are shared with everyone in this tenant and open from Home.
        </Typography>

        {shortcuts.sharedConflict ? (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={shortcuts.reloadShared}>
                Reload and review
              </Button>
            }
          >
            {shortcuts.sharedConflict.message}
          </Alert>
        ) : null}

        {shortcuts.loading ? (
          <CircularProgress size={28} />
        ) : shortcuts.error ? (
          <Alert severity="error">{shortcuts.error}</Alert>
        ) : !canManage ? (
          <Alert severity="info">You are not allowed to manage shortcuts for this tenant.</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {sharedTabs.map(tab => (
                <Button
                  key={tab.id}
                  variant={tab.id === selectedTabId ? 'contained' : 'outlined'}
                  onClick={() => setSelectedTabId(tab.id)}
                >
                  {tab.title}
                </Button>
              ))}
              <Button variant="outlined" onClick={addTab}>Add tab</Button>
            </Stack>

            {selectedTab ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Tab name"
                  size="small"
                  value={draftTitle}
                  onChange={event => setDraftTitle(event.target.value)}
                />
                <Button onClick={renameSelected}>Rename tab</Button>
                <Button onClick={() => moveSelectedTab(-1)}>Move tab left</Button>
                <Button onClick={() => moveSelectedTab(1)}>Move tab right</Button>
                <Button color="error" onClick={deleteSelected}>Delete tab</Button>
                <Button variant="contained" onClick={() => { setEditorShortcut(null); setEditorOpen(true); }}>
                  Add shortcut
                </Button>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Add a tab, then add the links people should open from Home.
              </Typography>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={() => setSuppressOpen(true)}
              onDragCancel={() => setSuppressOpen(false)}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={tabShortcuts.map(item => item.id)} strategy={rectSortingStrategy}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tabShortcuts.map(shortcut => (
                    <ShortcutTile
                      key={shortcut.id}
                      shortcut={shortcut}
                      tenantId={tenantId}
                      canDrag
                      canEdit
                      allowOpen={false}
                      suppressOpen={suppressOpen}
                      onOpen={() => {}}
                      onEdit={() => {
                        setEditorShortcut(shortcut);
                        setEditorOpen(true);
                      }}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          </Stack>
        )}
      </Box>
      <ShortcutEditorDialog
        open={editorOpen}
        shortcut={editorShortcut}
        tenantId={tenantId}
        imageScope={SCOPE_SHARED}
        onClose={() => setEditorOpen(false)}
        onSave={saveShortcut}
        onDelete={deleteShortcut}
      />
    </>
  );
  if (embedded) return content;
  return <MainLayout>{content}</MainLayout>;
}
